import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { api } from '../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C'];

// Stat Card Component
const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h4" component="h2" color={`${color}.main`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AuditLogs = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueActions, setUniqueActions] = useState([]);
  
  // Dashboard state
  const [dashboardData, setDashboardData] = useState({
    statistics: null,
    loading: true,
    error: null
  });
  
  useEffect(() => {
    fetchLogs();
    fetchDashboardData();
  }, []);
  
  useEffect(() => {
    // Filter logs based on search term and filters
    let filtered = logs;
    
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        (log.username && log.username.toLowerCase().includes(searchTermLower)) ||
        (log.action_type && log.action_type.toLowerCase().includes(searchTermLower)) ||
        (log.entity_type && log.entity_type.toLowerCase().includes(searchTermLower)) ||
        (log.ip_address && log.ip_address.toLowerCase().includes(searchTermLower)) ||
        (log.details && JSON.stringify(log.details).toLowerCase().includes(searchTermLower))
      );
    }
    
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action_type === actionFilter);
    }
    
    if (userFilter !== 'all') {
      filtered = filtered.filter(log => log.username === userFilter);
    }
    
    setFilteredLogs(filtered);
    setPage(0); // Reset to first page when filters change
  }, [logs, searchTerm, actionFilter, userFilter]);
  
  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/audit-logs/');
      
      let logData = [];
      if (response.data && response.data.results && Array.isArray(response.data.results)) {
        logData = response.data.results;
      } else if (response.data && Array.isArray(response.data)) {
        logData = response.data;
      } else {
        console.error('Unexpected response structure:', response);
        setError('Failed to load audit logs: Unexpected data structure');
      }
      
      if (logData.length > 0) {
        setLogs(logData);
        setFilteredLogs(logData);
        
        // Extract unique users and actions for filters
        const users = new Set(logData.map(log => log.username || ''));
        const actions = new Set(logData.map(log => log.action_type || ''));
        setUniqueUsers([...users].filter(Boolean));
        setUniqueActions([...actions].filter(Boolean));
      } else {
        setLogs([]);
        setFilteredLogs([]);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs: ' + (err.response?.data?.detail || err.message));
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: null }));
      
      // Fetch audit statistics
      const statsResponse = await api.get('/audit-logs/statistics/');
      
      setDashboardData({
        statistics: statsResponse.data || statsResponse,
        loading: false,
        error: null
      });
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setDashboardData(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Failed to load dashboard data: ' + (err.message || 'Unknown error')
      }));
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  const handleActionFilterChange = (event) => {
    setActionFilter(event.target.value);
  };
  
  const handleUserFilterChange = (event) => {
    setUserFilter(event.target.value);
  };
  
  const handleRefresh = () => {
    fetchLogs();
    fetchDashboardData();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'warning';
      case 'DELETE': return 'error';
      case 'LOGIN': return 'info';
      case 'LOGIN_FAILED': return 'error';
      case 'VIEW': return 'default';
      case 'ACTION': return 'primary';
      default: return 'default';
    }
  };

  // Action Breakdown Chart
  const ActionBreakdownChart = () => {
    const actionData = dashboardData.statistics?.action_breakdown || [];
    const chartData = actionData.map(item => ({
      name: item.action_type,
      value: item.count,
      percentage: ((item.count / (dashboardData.statistics?.total_logs || 1)) * 100).toFixed(1)
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percentage }) => `${name} (${percentage}%)`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [value.toLocaleString(), 'Count']} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // Entity Breakdown Chart  
  const EntityBreakdownChart = () => {
    const entityData = dashboardData.statistics?.entity_breakdown || [];
    const chartData = entityData.map(item => ({
      entity: item.entity_type,
      count: item.count
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="horizontal">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis type="category" dataKey="entity" width={100} />
          <Tooltip formatter={(value) => [value.toLocaleString(), 'Count']} />
          <Bar dataKey="count" fill={theme.palette.secondary.main} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Hourly Activity Chart
  const HourlyActivityChart = () => {
    const hourlyData = dashboardData.statistics?.hourly_activity || [];
    const chartData = hourlyData.map(item => ({
      hour: `${item.hour}:00`,
      count: item.count
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="hour" />
          <YAxis />
          <Tooltip formatter={(value) => [value.toLocaleString(), 'Activities']} />
          <Bar dataKey="count" fill={theme.palette.primary.main} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Page Views Chart
  const PageViewsChart = () => {
    const pageViews = logs
      .filter(log => log.entity_type === 'Page' && log.details?.page_name)
      .reduce((acc, log) => {
        const pageName = log.details.page_name;
        acc[pageName] = (acc[pageName] || 0) + 1;
        return acc;
      }, {});

    const chartData = Object.entries(pageViews)
      .map(([page, count]) => ({ page: page.substring(0, 20), count, fullPage: page }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="horizontal">
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis type="category" dataKey="page" width={120} />
          <Tooltip 
            formatter={(value) => [value.toLocaleString(), 'Views']}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullPage || label}
          />
          <Bar dataKey="count" fill={theme.palette.success.main} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Dashboard Content
  const DashboardContent = () => {
    const { statistics, loading, error } = dashboardData;

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    return (
      <Box>
        {/* Overview Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Logs"
              value={statistics?.total_logs || logs.length}
              icon={<AssessmentIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unique Users"
              value={statistics?.unique_users || uniqueUsers.length}
              icon={<PeopleIcon />}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Unique Agencies"
              value={statistics?.unique_agencies || 0}
              icon={<SecurityIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Period"
              value={statistics?.first_log && statistics?.last_log ? 
                `${Math.ceil((new Date(statistics.last_log) - new Date(statistics.first_log)) / (1000 * 60 * 60 * 24))} days` : 
                'N/A'
              }
              icon={<ScheduleIcon />}
              color="warning"
            />
          </Grid>
        </Grid>

        {/* First Row of Charts */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Actions by Type
                </Typography>
                <ActionBreakdownChart />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Entity Types
                </Typography>
                <EntityBreakdownChart />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Second Row of Charts */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Activity by Hour (Last 24h)
                </Typography>
                <HourlyActivityChart />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Most Viewed Pages
                </Typography>
                <PageViewsChart />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Table Content
  const TableContent = () => (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search logs..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: '200px' }}
        />
        
        <FormControl sx={{ minWidth: '150px' }} size="small">
          <InputLabel>Action Type</InputLabel>
          <Select
            value={actionFilter}
            onChange={handleActionFilterChange}
            label="Action Type"
          >
            <MenuItem value="all">All Actions</MenuItem>
            {uniqueActions.map(action => (
              <MenuItem key={action} value={action}>{action}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl sx={{ minWidth: '150px' }} size="small">
          <InputLabel>User</InputLabel>
          <Select
            value={userFilter}
            onChange={handleUserFilterChange}
            label="User"
          >
            <MenuItem value="all">All Users</MenuItem>
            {uniqueUsers.map(user => (
              <MenuItem key={user} value={user}>{user}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity Type</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredLogs
                ).map((log, index) => (
                  <TableRow key={log.log_id || index}>
                    <TableCell>{formatDate(log.event_timestamp)}</TableCell>
                    <TableCell>{log.username || 'System'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={log.action_type || 'Unknown'} 
                        size="small" 
                        color={getActionColor(log.action_type)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{log.entity_type || '-'}</TableCell>
                    <TableCell>{log.ip_address || '-'}</TableCell>
                    <TableCell>
                      {log.details ? (
                        <Box>
                          {log.details.page_name && (
                            <Typography variant="body2">
                              <strong>Page:</strong> {log.details.page_name}
                            </Typography>
                          )}
                          {log.details.page_url && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {log.details.page_url}
                            </Typography>
                          )}
                          {log.details.activity_type && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              <strong>Activity:</strong> {log.details.activity_type}
                            </Typography>
                          )}
                          {log.details.login_success !== undefined && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: log.details.login_success ? 'success.main' : 'error.main' }}>
                              <strong>Login:</strong> {log.details.login_success ? 'Success' : 'Failed'}
                            </Typography>
                          )}
                          {log.details.username_attempted && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              <strong>Username:</strong> {log.details.username_attempted}
                            </Typography>
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                
                {filteredLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
            component="div"
            count={filteredLogs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
  
  return (
    <Container maxWidth="lg">
      <Paper elevation={2} sx={{ p: 4, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Audit Logs
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading || dashboardData.loading}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab 
              label="Dashboard" 
              icon={<AnalyticsIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Logs Table" 
              icon={<TableChartIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {activeTab === 0 && <DashboardContent />}
        {activeTab === 1 && <TableContent />}
      </Paper>
    </Container>
  );
};

export default AuditLogs; 